import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Loader2, Sparkles, Copy, Share2, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

function ShareBoost({ card, className = "" }) {
  const link = card.referral_link;
  const copy = () => { if (!link) return; navigator.clipboard?.writeText(link); toast.success("Referral link copied"); };
  const share = async () => {
    if (!link) return;
    if (navigator.share) { try { await navigator.share({ title: "Velocity Driver Card", text: "Drive with MY VELOCITY — use my link to sign up", url: link }); } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share"); } }
    else copy();
  };
  return (
    <div className={`space-y-2 w-full ${className}`} data-testid="driver-card-share-boost">
      <p className="text-sm text-cyan-300 font-mono" data-testid="driver-card-waitlisted">On the waitlist · #{card.waitlist_position}{card.boost > 0 && <span className="text-emerald-300 ml-2 inline-flex items-center gap-0.5"><ArrowUp className="w-3 h-3" />{card.boost} from {card.referrals} referral{card.referrals === 1 ? "" : "s"}</span>}</p>
      <p className="text-xs text-slate-400">Move up <span className="text-slate-200">{card.spots_per_referral} spots</span> for every driver who signs up with your link.</p>
      {link ? (
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate text-xs font-mono px-2 py-1.5 rounded bg-slate-900 border border-slate-800 text-cyan-300" data-testid="driver-card-referral-link">{link}</code>
          <Button size="sm" variant="outline" onClick={copy} data-testid="driver-card-copy-btn" className="bg-transparent border-slate-700 h-8 px-2"><Copy className="w-3.5 h-3.5" /></Button>
          <Button size="sm" onClick={share} data-testid="driver-card-share-btn" className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 h-8 px-2"><Share2 className="w-3.5 h-3.5" /></Button>
        </div>
      ) : <Link to="/delivery" className="text-xs text-cyan-300 underline" data-testid="driver-card-get-link">Get your referral link on the Delivery board →</Link>}
    </div>
  );
}

export function WaitlistButton({ card, onJoined, className = "" }) {
  const [busy, setBusy] = useState(false);
  const join = async () => {
    setBusy(true);
    try { const { data } = await api.post("/driver-card/waitlist", {}); toast.success(data.already ? `You're already on the list — #${data.position}` : `You're #${data.position} on the Driver Card waitlist`); onJoined?.(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not join waitlist"); } finally { setBusy(false); }
  };
  if (card?.waitlisted) return <ShareBoost card={card} className={className} />;
  return <Button onClick={join} disabled={busy} data-testid="driver-card-waitlist-btn" className={`bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-semibold ${className}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" />Join the waitlist</>}</Button>;
}

export default function DriverCardPanel() {
  const [card, setCard] = useState(null);
  useEffect(() => { api.get("/driver-card/me").then((r) => setCard(r.data)).catch(() => {}); }, []);
  if (!card) return null;
  return (
    <section className="rounded-2xl bg-[#131722] border border-slate-800 overflow-hidden grid lg:grid-cols-[1.3fr_1fr]" data-testid="driver-card-panel">
      <img src="/driver-card-concept.png" alt="Velocity Driver Card concept — front and back" loading="lazy" data-testid="driver-card-concept-image"
        className="w-full h-full object-cover min-h-[240px]" />
      <div className="p-6 lg:p-8 space-y-3 flex flex-col justify-center">
        <p className="font-mono text-xs tracking-widest uppercase text-cyan-400">// Velocity Driver Card</p>
        <h3 className="font-display text-2xl font-extrabold tracking-tight">Metal. Tap-to-pay. Built for the road ahead.</h3>
        <p className="text-sm text-slate-400">{card.eligible ? `Reserve your spot, ${card.name.split(" ")[0]}. Verified drivers and Driver Pass members get first access when cards ship.` : "Get verified as a driver or join Driver Pass to move up the list when cards ship."}</p>
        <div className="flex flex-wrap gap-3 items-center pt-1">
          <WaitlistButton card={card} onJoined={() => api.get("/driver-card/me").then((r) => setCard(r.data))} />
          {!card.eligible && <Link to="/membership"><Button variant="outline" className="bg-transparent border-slate-700" data-testid="driver-card-get-pass">See Driver Pass</Button></Link>}
        </div>
        <p className="text-[11px] text-slate-600">Concept design · not an issued payment card.</p>
      </div>
    </section>
  );
}
